<script setup lang="ts">
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import Skeleton from '@/shared/ui/Skeleton/index';
import { getMonth } from '@/shared/utils/getMonth';
import { getNameWeekDay } from '@/shared/utils/getNameWeekDay';
import { isClosedDayStatus } from '@/entities/appointment/lib/scheduleStatus';
import type { ILawyerDetail } from '@/entities/lawyer/model/types/lawyer';
import type { IScheduleDay } from '@/entities/appointment/model/types';

interface DayChip {
  id: string;
  value: string;
  isOnline: boolean;
  status: number;
  weekDay: string;
  month: string;
}

const props = withDefaults(
  defineProps<{
    lawyer: ILawyerDetail | null;
    scheduleDays?: IScheduleDay[];
    pending?: boolean;
  }>(),
  { scheduleDays: () => [], pending: false },
);
const emit = defineEmits<{
  (e: 'select', payload: { date: string; isOnline: boolean }): void;
}>();

const isMobile: boolean = useIsMobile();
const modalStore = useModalStore();

const manualDate: Ref<string> = ref('');
const isCalendarMode: Ref<boolean> = ref(false);

const isClosedDay = (day: DayChip): boolean => isClosedDayStatus(day.status);

const rawDays: ComputedRef<IScheduleDay[]> = computed(() => props.scheduleDays);
const pendingState: ComputedRef<boolean> = computed(() => props.pending);

const toDayChip = (el: IScheduleDay): DayChip => {
  const date = new Date(el.date);
  return {
    id: el.date,
    value: el.date,
    isOnline: el.is_online,
    status: el.status,
    weekDay: getNameWeekDay(date.getDay()),
    month: `${date.getDate()} ${getMonth(date.getMonth(), true)}`,
  };
};

const days: ComputedRef<DayChip[]> = computed(() => (rawDays.value ?? []).map(toDayChip));

const firstAvailableDate: ComputedRef<string> = computed(
  () => (days.value.find((day) => !isClosedDay(day)) ?? days.value[0])?.value ?? '',
);

const checkedDate = computed<string>(() => {
  if (isCalendarMode.value) return '';
  return manualDate.value || firstAvailableDate.value;
});

const radioValue = computed<string>({
  get: () => checkedDate.value,
  set: (value: string) => {
    isCalendarMode.value = false;
    manualDate.value = value;
  },
});

const emitSelect = (): void => {
  const day = days.value?.find((d) => d.value === checkedDate.value);
  emit('select', { date: checkedDate.value, isOnline: Boolean(day?.isOnline) });
};

const openCalendar = (): void => {
  isCalendarMode.value = true;
  modalStore.setModalState('dateTime', true, { lawyerInfo: props.lawyer });
};

const onCheckedDateChange = (value: string): void => {
  if (import.meta.client && value) emitSelect();
};

watch(checkedDate, onCheckedDateChange);

watch(
  () => modalStore.modalState.dateTime,
  (isOpen: boolean): void => {
    if (isOpen || !isCalendarMode.value) return;
    isCalendarMode.value = false;
    manualDate.value = '';
  },
);

onMounted(() => {
  if (checkedDate.value) emitSelect();
});
</script>

<template>
  <div :class="['date-selector', { _mobile: isMobile }]">
    <div v-if="pendingState" class="date-selector__near">
      <Skeleton
        v-for="n in 3"
        :key="`day-skeleton-${n}`"
        class="date-selector__item"
        width="100%"
        height="52px"
      />
      <Skeleton class="date-selector__item" width="100%" height="52px" />
    </div>
    <div v-else class="date-selector__near">
      <div
        v-for="day in days ?? []"
        :key="day.id"
        :class="['date-selector__item', { _closed: isClosedDay(day) }]"
      >
        <input
          :id="`date-selector-${day.id}`"
          v-model="radioValue"
          type="radio"
          name="date"
          :value="day.value"
          class="date-selector__input"
        />
        <label class="date-selector__label" :for="`date-selector-${day.id}`">
          <div class="date-selector__text-wrapper">
            <span class="date-selector__title">{{ day.weekDay }}</span>
            <span class="date-selector__text">{{ day.month }}</span>
          </div>
          <AppIcon
            :name="day.isOnline ? 'Laptop' : 'Office'"
            class="date-selector__icon"
          />
        </label>
      </div>
      <div :class="['date-selector__item', '_calendar', { _active: isCalendarMode }]">
        <button
          type="button"
          class="date-selector__button _calendar"
          aria-label="Открыть календарь"
          @click.prevent="openCalendar"
        >
          <AppIcon name="Calendar" />
          <span v-if="!isMobile" class="date-selector__button-text">Календарь</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.date-selector {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  &__near {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 3px;
    width: 100%;

    @media @1366 {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__item {
    height: 52px;

    &._calendar {
      padding: 0;
      background: white;
      border: 1px solid @color-gallery;
      border-radius: @main-size;

      &._active {
        background: @color-sauvignon;
        border: 1px solid @color-persimmon;
        box-shadow:
          -@main-size -5px 25px rgba(0, 0, 0, 0.06),
          0px @size-x10 @size-x10 -18px rgba(139, 144, 164, 0.2);
      }
    }

    &._closed {
      pointer-events: none;

      .date-selector__title,
      .date-selector__text {
        color: @color-alto;
      }

      .date-selector__icon path {
        fill: @color-alto;
      }

      .date-selector__label {
        border-color: @color-alto;
      }
    }
  }

  &__input {
    display: none;
  }

  &__label {
    background: white;
    border: 1px solid @color-gallery;
    display: flex;
    cursor: pointer;
    user-select: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: @main-size;
    width: 100%;
    height: 100%;
    padding: @main-size;
  }

  &__text-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: @size-x3;
    line-height: 130%;
    color: @color-elephant;
    text-transform: capitalize;
  }

  &__text {
    font-size: 10px;
    line-height: 130%;
    font-weight: 500;
    letter-spacing: 0.41px;
    color: @color-dove-gray;
  }

  &__input[type='radio']:checked + label {
    background: @color-sauvignon;
    border: 1px solid @color-persimmon;
    box-shadow:
      -@main-size -5px 25px rgba(0, 0, 0, 0.06),
      0px @size-x10 @size-x10 -18px rgba(139, 144, 164, 0.2);
  }

  &__button {
    width: 100%;
    height: 100%;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  &__button-text {
    color: @color-dove-gray;
    margin-top: 3px;
    display: inline-block;
    font-size: @size-x3;
    font-weight: 500;
    line-height: 130%;
  }

  &__icon {
    width: 15px;
    height: 15px;
    margin-left: auto;
  }

  &._mobile &__near {
    width: 100%;
    grid-template-columns: repeat(3, 1fr);
    gap: @size-x2;
  }

  &._mobile &__item {
    max-width: 100%;
    width: auto;

    &._calendar {
      position: absolute;
      top: @size-x4;
      right: @size-x4;
    }
  }

  &._mobile &__button {
    padding: @size-x3;
  }
}
</style>
